import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, PhoneCall, MessageCircle, LogOut, TrendingUp, Target, CheckCircle2, Clock
} from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(user ? false : true);
  const [tab, setTab] = useState("overview");

  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [chats, setChats] = useState([]);
  const [chatMessages, setChatMessages] = useState({ sessionId: null, items: [] });

  useEffect(() => {
    if (user) return;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        navigate("/login", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
  }, [user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [o, l, c, s] = await Promise.all([
        api.get("/analytics/overview"),
        api.get("/leads"),
        api.get("/callbacks"),
        api.get("/chat/sessions"),
      ]);
      setOverview(o.data);
      setLeads(l.data);
      setCallbacks(c.data);
      setChats(s.data);
    } catch (err) {
      // 401s redirect via auth-me effect above; surface other failures so they're not silent.
      if (err?.response?.status !== 401) {
        console.error("Dashboard loadAll failed", err);
        toast.error("Could not load dashboard data — please refresh.");
      }
    }
  }, []);

  useEffect(() => {
    if (user) loadAll();
  }, [user, loadAll]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout failed (continuing anyway)", err);
    }
    navigate("/login", { replace: true });
  };

  const updateLead = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(`Lead marked ${status}`);
      loadAll();
    } catch { toast.error("Update failed"); }
  };

  const updateCallback = async (id, status) => {
    try {
      await api.patch(`/callbacks/${id}`, { status });
      toast.success(`Callback marked ${status}`);
      loadAll();
    } catch { toast.error("Update failed"); }
  };

  const openChatHistory = async (session_id) => {
    const { data } = await api.get(`/chat/sessions/${session_id}/messages`);
    setChatMessages({ sessionId: session_id, items: data });
  };

  const monthlyProgress = useMemo(() => {
    if (!overview) return 0;
    return Math.min(100, Math.round((overview.total_leads / (overview.monthly_target || 100)) * 100));
  }, [overview]);

  if (checking) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-ink/70 text-sm">Checking session…</div>;
  }

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-forest text-cream p-6 sticky top-0 h-screen" data-testid="dashboard-sidebar">
        <div className="flex items-center gap-2 mb-10">
          <span className="w-9 h-9 rounded-full bg-clay flex items-center justify-center font-display text-lg">L</span>
          <div>
            <div className="font-display text-lg leading-none">Patient Ops</div>
            <div className="text-[10px] tracking-widest uppercase text-sage">Dr Louw Practice</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 text-sm">
          {[
            { k: "overview", label: "Overview", Icon: LayoutDashboard },
            { k: "leads", label: "Leads", Icon: Users },
            { k: "callbacks", label: "Callbacks", Icon: PhoneCall },
            { k: "chats", label: "Chatbot", Icon: MessageCircle },
          ].map(({ k, label, Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${tab === k ? "bg-cream/10 text-cream" : "text-cream/60 hover:text-cream hover:bg-cream/5"}`}
              data-testid={`sidebar-${k}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>

        <div className="mt-6 pt-6 border-t border-cream/10">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover"/>
            ) : (
              <div className="w-9 h-9 rounded-full bg-clay/30 flex items-center justify-center">{user?.name?.[0] || "A"}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[11px] text-cream/60 truncate">{user?.email}</div>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="w-full bg-transparent border-cream/20 text-cream hover:bg-cream hover:text-forest" data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2"/> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-5 sm:p-8 max-w-[1400px] w-full mx-auto">
        {/* Mobile tabs */}
        <div className="lg:hidden mb-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="overview" data-testid="mobile-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="leads" data-testid="mobile-tab-leads">Leads</TabsTrigger>
              <TabsTrigger value="callbacks" data-testid="mobile-tab-callbacks">Calls</TabsTrigger>
              <TabsTrigger value="chats" data-testid="mobile-tab-chats">Chats</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="eyebrow">Dashboard</p>
            <h1 className="text-3xl sm:text-4xl font-display text-forest mt-1 capitalize">{tab}</h1>
          </div>
          <Button onClick={loadAll} variant="outline" className="border-border" data-testid="refresh-button">Refresh</Button>
        </div>

        {tab === "overview" && overview && (
          <div className="space-y-6" data-testid="overview-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total leads" value={overview.total_leads} Icon={Users} accent="bg-clay/10 text-clay" testId="stat-leads" />
              <StatCard label="New today" value={overview.new_leads} Icon={TrendingUp} accent="bg-ochre/15 text-ochre" testId="stat-new" />
              <StatCard label="Pending callbacks" value={overview.pending_callbacks} Icon={PhoneCall} accent="bg-sage text-forest" testId="stat-callbacks" />
              <StatCard label="Chatbot sessions" value={overview.total_chats} Icon={MessageCircle} accent="bg-forest/10 text-forest" testId="stat-chats" />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-border rounded-2xl p-6">
                <p className="eyebrow">Leads · last 14 days</p>
                <div className="h-64 mt-4" data-testid="leads-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={overview.daily_series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2DCD5"/>
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#7A7571" }} tickFormatter={(d) => d.slice(5)}/>
                      <YAxis tick={{ fontSize: 11, fill: "#7A7571" }} allowDecimals={false}/>
                      <Tooltip cursor={{ fill: "rgba(178,95,76,0.08)" }}/>
                      <Bar dataKey="leads" fill="#B25F4C" radius={[6,6,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-border rounded-2xl p-6">
                <p className="eyebrow">Monthly target</p>
                <div className="mt-4 flex items-end gap-2">
                  <span className="font-display text-5xl text-forest">{overview.total_leads}</span>
                  <span className="text-sm text-ink/60 mb-2">/ {overview.monthly_target} patients</span>
                </div>
                <div className="mt-4 h-2.5 bg-cream-alt rounded-full overflow-hidden">
                  <div className="h-full bg-clay" style={{ width: `${monthlyProgress}%` }}/>
                </div>
                <p className="text-xs text-ink/60 mt-2"><Target className="w-3 h-3 inline mr-1"/> {monthlyProgress}% of monthly goal</p>

                <div className="mt-8">
                  <p className="eyebrow">Conversion rate</p>
                  <div className="font-display text-4xl text-forest mt-2">{overview.conversion_rate}%</div>
                  <p className="text-xs text-ink/60 mt-1">{overview.converted_leads} of {overview.total_leads} leads converted</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-border rounded-2xl p-6">
              <p className="eyebrow">Leads by source</p>
              <div className="mt-4 flex flex-wrap gap-3" data-testid="by-source">
                {(overview.by_source || []).length === 0 && <p className="text-sm text-ink/60">No leads yet.</p>}
                {(overview.by_source || []).map((s) => (
                  <div key={s.source} className="px-4 py-2 bg-cream-alt rounded-full text-sm text-forest">
                    <span className="uppercase tracking-widest text-[10px] mr-2 text-ink/60">{s.source}</span>
                    <span className="font-display text-lg">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="leads-tab">
            {leads.length === 0 ? (
              <EmptyState label="No leads yet. Share your Instagram/TikTok campaigns to start!"/>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-cream-alt/70">
                    <tr className="text-left text-[11px] uppercase tracking-widest text-ink/70">
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Contact</th>
                      <th className="px-5 py-3">Service</th>
                      <th className="px-5 py-3">Source</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => (
                      <tr key={l.id} className="border-t border-border" data-testid={`lead-row-${l.id}`}>
                        <td className="px-5 py-3 font-medium">{l.name}</td>
                        <td className="px-5 py-3">
                          <a className="text-clay block" href={`tel:${l.phone}`}>{l.phone}</a>
                          {l.email && <a className="text-ink/60 text-xs" href={`mailto:${l.email}`}>{l.email}</a>}
                        </td>
                        <td className="px-5 py-3">{l.service || "—"}</td>
                        <td className="px-5 py-3"><Badge variant="outline" className="capitalize">{l.source || "direct"}</Badge></td>
                        <td className="px-5 py-3"><StatusBadge status={l.status}/></td>
                        <td className="px-5 py-3 text-right space-x-1">
                          <Button size="sm" variant="outline" className="border-border" onClick={() => updateLead(l.id, "contacted")} data-testid={`lead-contacted-${l.id}`}>Contacted</Button>
                          <Button size="sm" className="bg-forest hover:bg-forest-dark text-cream" onClick={() => updateLead(l.id, "converted")} data-testid={`lead-converted-${l.id}`}>Converted</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "callbacks" && (
          <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="callbacks-tab">
            {callbacks.length === 0 ? (
              <EmptyState label="No callbacks requested yet."/>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-cream-alt/70">
                    <tr className="text-left text-[11px] uppercase tracking-widest text-ink/70">
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Phone</th>
                      <th className="px-5 py-3">Best time</th>
                      <th className="px-5 py-3">Reason</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callbacks.map((c) => (
                      <tr key={c.id} className="border-t border-border" data-testid={`cb-row-${c.id}`}>
                        <td className="px-5 py-3 font-medium">{c.name}</td>
                        <td className="px-5 py-3"><a className="text-clay" href={`tel:${c.phone}`}>{c.phone}</a></td>
                        <td className="px-5 py-3">{c.best_time}</td>
                        <td className="px-5 py-3 max-w-[22ch] truncate" title={c.reason}>{c.reason || "—"}</td>
                        <td className="px-5 py-3"><StatusBadge status={c.status}/></td>
                        <td className="px-5 py-3 text-right">
                          <Button size="sm" className="bg-forest hover:bg-forest-dark text-cream" onClick={() => updateCallback(c.id, "called")} data-testid={`cb-done-${c.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1"/> Mark called
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "chats" && (
          <div className="grid md:grid-cols-[380px_1fr] gap-6" data-testid="chats-tab">
            <div className="bg-white border border-border rounded-2xl overflow-hidden">
              {chats.length === 0 ? (
                <EmptyState label="No chats yet."/>
              ) : (
                <ul className="divide-y divide-border max-h-[70vh] overflow-y-auto scroll-slim">
                  {chats.map((s) => (
                    <li key={s.session_id}>
                      <button
                        onClick={() => openChatHistory(s.session_id)}
                        className={`w-full text-left px-5 py-4 hover:bg-cream-alt/60 ${chatMessages.sessionId === s.session_id ? "bg-cream-alt" : ""}`}
                        data-testid={`chat-session-${s.session_id}`}
                      >
                        <div className="text-xs text-ink/60 flex items-center gap-2">
                          <Clock className="w-3 h-3"/>
                          {new Date(s.last_message_at).toLocaleString()}
                        </div>
                        <div className="text-sm text-forest mt-1 truncate">Session · {s.session_id}</div>
                        <div className="text-[11px] tracking-widest uppercase text-clay mt-1">{s.message_count || 0} messages</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white border border-border rounded-2xl p-6 min-h-[300px]">
              {chatMessages.items.length === 0 ? (
                <p className="text-sm text-ink/60">Select a chat session from the list.</p>
              ) : (
                <div className="space-y-3 max-h-[70vh] overflow-y-auto scroll-slim">
                  {chatMessages.items.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] text-sm px-4 py-2 rounded-2xl ${m.role === "user" ? "bg-clay text-cream rounded-br-sm" : "bg-cream-alt text-ink rounded-bl-sm"}`}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, Icon, accent, testId }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5" data-testid={testId}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4"/>
      </div>
      <div className="font-display text-3xl text-forest mt-4">{value ?? 0}</div>
      <p className="text-[11px] uppercase tracking-widest text-ink/60 mt-1">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "bg-clay/15 text-clay",
    contacted: "bg-ochre/20 text-ochre",
    converted: "bg-sage text-forest",
    pending: "bg-clay/15 text-clay",
    called: "bg-sage text-forest",
  };
  return <span className={`px-2.5 py-1 text-[11px] uppercase tracking-widest rounded-full font-semibold ${map[status] || "bg-cream-alt text-ink"}`}>{status}</span>;
}

function EmptyState({ label }) {
  return <div className="px-8 py-16 text-center text-sm text-ink/60">{label}</div>;
}
